import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';

// Importaciones de entornos.
import { environment } from '../../../environments/environment';

// Importaciones de servicios.
import { AuthService } from '../../services/auth.service';
import { LogService } from '../../services/log.service';
import { MeService } from '../../services/me.service';

// Importaciones de interfaces.
import { IEmployee } from '../../interfaces/employee.interface';

// Importaciones de modelos.
import { Employee } from '../../models/employee.model';
import { Log } from '../../models/log.model';

/**
 * Componente de encabezado.
 *
 * Este componente representa el encabezado de la aplicación, donde se pueden incluir elementos como el logo,
 * la navegación principal, botones de acceso y otros elementos interactivos.
 */
@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  standalone: false,
})
export class HeaderComponent implements OnInit {
  /**
   * Indicador de carga.
   *
   * Esta propiedad se utiliza para mostrar o ocultar indicadores de carga en la interfaz.
   * Es `true` cuando se está realizando una operación en segundo plano (como una solicitud de datos),
   * y `false` cuando la operación ha finalizado.
   */
  loading: boolean = false;

  /**
   * Propiedad que almacena una instancia de la clase Employee.
   * Se inicializa con un nuevo objeto Employee vacío.
   */
  profile: Employee = new Employee();

  /**
   * Crea una instancia de la clase.
   *
   * Este constructor inicializa los servicios necesarios para la clase,
   * incluyendo el servicio de autenticación (`AuthService`), el servicio
   * para obtener información del usuario (`MeService`) y el enrutador
   * (`Router`) para manejar la navegación en la aplicación.
   *
   * @param authService - Servicio encargado de la autenticación del usuario.
   * @param logService - Servicio de registros para gestionar los accesos.
   * @param meService - Servicio utilizado para obtener la información del usuario actual.
   * @param router - Servicio para gestionar la navegación en la aplicación.
   */
  constructor(
    private authService: AuthService,
    private logService: LogService,
    private meService: MeService,
    private router: Router
  ) {}

  /**
   * Método que se ejecuta al inicializar el componente.
   * Llama al método getMe() para obtener los datos del usuario autenticado.
   */
  async ngOnInit(): Promise<void> {
    await this.getProfile();
  }

  /**
   * Método asíncrono que obtiene la información del usuario actual.
   * Retorna una promesa que no devuelve ningún valor (void).
   */
  async getProfile(): Promise<void> {
    this.loading = true;
    this.profile = new Employee();

    try {
      const meResponse: IEmployee = await this.meService.get();
      this.profile.displayName = meResponse.displayName;
      this.profile.username = meResponse.username;
    } finally {
      this.loading = false;
    }
  }

  /**
   * Cierra la sesión del usuario actual.
   *
   * Este método elimina el token de sesión del usuario utilizando el servicio de autenticación,
   * registra un log indicando que el usuario ha cerrado sesión, y redirige al usuario a la página
   * de inicio de sesión. Si ocurre un error al registrar el log, se muestra un mensaje de advertencia
   * en la consola en entornos no productivos.
   */
  async logout(): Promise<void> {
    // Añadir un registro de cierre de sesión.
    try {
      await this.logService.create(
        new Log({
          description: 'Ha cerrado la sesión.',
        })
      );
    } catch (errors: any) {
      if (!environment.production) {
        console.warn(
          'No se ha podido guardar el registro de cierre de sesión.'
        );
      }
    }

    // Eliminar el token de sesión del usuario.
    this.authService.removeToken();

    // Redirigir al usuario a la página de inicio de sesión.
    this.router.navigateByUrl('/login');
  }
}
